import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
type Section = {
  title: string;
  items: string[];
};
type Chapter = {
  title: string;
  pages: string;
  sections: Section[];
};
const chapters: Chapter[] = [{
  title: 'El ADN del 5: rol, lectura y dominio del centro del campo',
  pages: '60 páginas',
  sections: [{
    title: '1.1 Qué significa ser "el 5" en el fútbol moderno',
    items: ['El 5 como equilibrio del equipo', 'Diferencia entre correr mucho y cubrir bien', 'Por qué el 5 juega antes de recibir la pelota', 'El mediocampista defensivo como cerebro silencioso', 'El 5 como primer defensor y primer constructor']
  }, {
    title: '1.2 Tipos de 5 según el modelo de juego',
    items: ['5 posicional', '5 recuperador', '5 mixto', '5 organizador', '5 agresivo de presión', '5 de salida limpia', '5 de doble pivote', '5 de contención pura']
  }, {
    title: '1.3 Responsabilidades principales del puesto',
    items: ['Proteger a los centrales', 'Tapar líneas de pase interiores', 'Ordenar la presión', 'Dar apoyo constante a los centrales y laterales', 'Ser opción de pase en salida', 'Recuperar segundas pelotas', 'Frenar contragolpes', 'Mantener el equilibrio ofensivo-defensivo']
  }, {
    title: '1.4 Zonas clave del mediocampista defensivo',
    items: ['Zona central delante de los centrales', 'Espalda de los interiores', 'Carriles interiores', 'Zona de rechace', 'Zona de cobertura lateral', 'Zona de presión tras pérdida', 'Zona de pase seguro']
  }, {
    title: '1.5 El mapa mental del 5',
    items: ['Qué mirar antes de recibir', 'Cómo detectar al rival libre', 'Cómo anticipar una jugada antes de que ocurra', 'Cómo leer el cuerpo del rival', 'Cómo decidir cuándo saltar y cuándo guardar posición', 'Cómo orientar al equipo desde el centro']
  }, {
    title: '1.6 Errores comunes de los mediocampistas defensivos',
    items: ['Perseguir la pelota sin cuidar la espalda', 'Meterse demasiado entre centrales', 'No perfilarse antes de recibir', 'Llegar tarde a las coberturas', 'Defender mirando solo la pelota', 'Salir a presionar cuando no hay respaldo', 'Jugar siempre hacia atrás', 'No comunicarse con los centrales']
  }, {
    title: '1.7 Perfil psicológico del 5',
    items: ['Calma bajo presión', 'Liderazgo sin necesidad de gritar', 'Personalidad para pedir la pelota', 'Inteligencia para cortar el ritmo rival', 'Concentración permanente', 'Tolerancia al error', 'Mentalidad de equilibrio']
  }, {
    title: '1.8 Checklist del capítulo',
    items: ['Qué debe dominar un 5 antes de avanzar', 'Autoevaluación inicial del jugador', 'Diagnóstico de fortalezas y debilidades', 'Preguntas para analizar tu juego actual']
  }]
}, {
  title: 'Posicionamiento, coberturas y roles alternos del 5',
  pages: '60 páginas',
  sections: [{
    title: '2.1 Posición base del 5 en fase defensiva',
    items: ['Ubicación entre líneas', 'Distancia ideal con centrales', 'Distancia ideal con interiores', 'Cómo achicar espacios sin romper el bloque', 'Cómo tapar el pase al 9 rival', 'Cómo orientar la presión hacia banda']
  }, {
    title: '2.2 El 5 en bloque bajo',
    items: ['Cómo proteger el área', 'Cómo cerrar la frontal', 'Cómo cubrir rechaces', 'Cómo evitar tiros desde media distancia', 'Cómo defender centros laterales', 'Cuándo meterse como tercer central', 'Cuándo salir al poseedor']
  }, {
    title: '2.3 El 5 en bloque medio',
    items: ['Cómo controlar la zona central', 'Cómo bascular según el lado de la pelota', 'Cómo tapar al mediapunta rival', 'Cómo cerrar líneas interiores', 'Cómo saltar sobre el volante rival', 'Cómo sostener al equipo sin retroceder de más']
  }, {
    title: '2.4 El 5 en presión alta',
    items: ['Cuándo acompañar la presión', 'Cómo bloquear al pivote rival', 'Cómo orientar la salida rival', 'Cómo saltar sin regalar la espalda', 'Coordinación con delanteros e interiores', 'Qué hacer si la presión es superada']
  }, {
    title: '2.5 El 5 como tercer central',
    items: ['Cuándo incrustarse entre centrales', 'Cómo formar línea de 3 en salida', 'Cómo defender transiciones', 'Cómo cubrir la espalda de los laterales', 'Cómo ganar duelos aéreos desde segunda línea', 'Riesgos de hundirse demasiado']
  }, {
    title: '2.6 El 5 como lateral de emergencia',
    items: ['Cuándo cubrir al lateral que sube', 'Cómo defender en banda sin ser lateral', 'Cómo orientar al extremo rival', 'Cómo evitar faltas innecesarias', 'Cómo cerrar el pase atrás', 'Cómo volver al centro después de la cobertura']
  }, {
    title: '2.7 El 5 como interior temporal',
    items: ['Cuándo ocupar el carril interior', 'Cómo aparecer como opción de pase', 'Cómo romper líneas sin desordenar al equipo', 'Cómo intercambiar posición con un interior', 'Cómo llegar a zona de remate sin perder equilibrio']
  }, {
    title: '2.8 El 5 en doble pivote',
    items: ['Reparto de tareas entre dos mediocentros', 'Quién salta y quién guarda', 'Cómo cubrirse entre ambos', 'Cómo dividir zonas', 'Cómo generar superioridad en salida', 'Cómo evitar quedar los dos en la misma línea']
  }, {
    title: '2.9 El 5 como pivote único',
    items: ['Mayor responsabilidad posicional', 'Cómo jugar con interiores altos', 'Cómo evitar quedar aislado', 'Cómo ofrecer línea de pase constante', 'Cómo controlar ambos lados del campo', 'Cómo comunicar y ordenar permanentemente']
  }, {
    title: '2.10 Errores posicionales que rompen al equipo',
    items: ['Saltar tarde', 'Saltar sin respaldo', 'Defender plano', 'Quedar de espaldas al juego', 'Dejar libre la frontal', 'No cerrar el carril central', 'Desconectarse tras pase propio']
  }, {
    title: '2.11 Fichas prácticas del capítulo',
    items: ['Mapa de coberturas del 5', 'Mapa de zonas prohibidas', 'Checklist de bloque bajo', 'Checklist de bloque medio', 'Checklist de presión alta', 'Ejercicios de posicionamiento sin pelota']
  }]
}, {
  title: 'Técnica y toma de decisiones con pelota',
  pages: '60 páginas',
  sections: [{
    title: '3.1 El 5 como salida del equipo',
    items: ['Cómo ofrecer apoyo a los centrales', 'Cómo recibir de cara', 'Cómo recibir perfilado', 'Cómo atraer presión', 'Cómo jugar simple sin ser predecible', 'Cómo decidir entre pase corto, pase vertical o cambio de frente']
  }, {
    title: '3.2 Perfil corporal del mediocampista defensivo',
    items: ['Recibir abierto', 'Mirar antes de controlar', 'Control orientado según presión', 'Proteger la pelota con el cuerpo', 'Primer toque útil', 'Cómo girar bajo presión', 'Cómo evitar controles muertos']
  }, {
    title: '3.3 Escaneo antes de recibir',
    items: ['Qué mirar antes del pase', 'Cuántas veces mirar', 'Cómo detectar presión cercana', 'Cómo detectar el hombre libre', 'Cómo reconocer el lado débil', 'Cómo saber cuándo jugar de primera']
  }, {
    title: '3.4 Pases esenciales del 5',
    items: ['Pase seguro al central', 'Pase al lateral', 'Pase vertical al interior', 'Pase filtrado al mediapunta', 'Cambio de orientación', 'Pase de descarga', 'Pase a tercer hombre', 'Pase tenso entre líneas']
  }, {
    title: '3.5 Juego a uno y dos toques',
    items: ['Cuándo jugar simple', 'Cuándo acelerar', 'Cuándo pausar', 'Cómo evitar perderla en zona peligrosa', 'Cómo usar el pase atrás para progresar', 'Cómo atraer para liberar']
  }, {
    title: '3.6 El 5 bajo presión',
    items: ['Cómo recibir con marca encima', 'Cómo usar el cuerpo para proteger', 'Cómo salir con giro', 'Cómo descargar de primera', 'Cómo provocar la presión rival', 'Cómo no entrar en pánico', 'Cómo elegir la opción menos riesgosa']
  }, {
    title: '3.7 Conducción estratégica',
    items: ['Cuándo conducir y cuándo pasar', 'Cómo romper primera línea rival', 'Cómo fijar a un rival', 'Cómo atraer para soltar', 'Cómo avanzar sin desordenar al equipo', 'Cómo conducir hacia espacios seguros']
  }, {
    title: '3.8 Cambios de orientación',
    items: ['Por qué el 5 debe cambiar el lado del ataque', 'Cómo detectar el lado débil', 'Técnica del pase largo', 'Cambio de frente raso', 'Cambio de frente aéreo', 'Cuándo no cambiar de orientación']
  }, {
    title: '3.9 El 5 en ataque posicional',
    items: ['Cómo ubicarse detrás de la jugada', 'Cómo sostener la posesión', 'Cómo estar listo para la pérdida', 'Cómo atacar sin abandonar la estructura', 'Cómo ofrecer pase de seguridad', 'Cómo cerrar el lado débil']
  }, {
    title: '3.10 Errores con pelota',
    items: ['Recibir de espaldas sin mirar', 'Jugar siempre hacia atrás', 'Forzar pases verticales', 'Conducir de más', 'No perfilarse', 'Perderla en zona central', 'No leer la presión']
  }, {
    title: '3.11 Ejercicios técnicos específicos',
    items: ['Rondo orientado para 5', 'Circuito de perfil corporal', 'Ejercicio de escaneo antes de recibir', 'Pase vertical bajo presión', 'Juego a tercer hombre', 'Cambios de orientación', 'Salida entre centrales', 'Recepción con marca a la espalda']
  }]
}, {
  title: 'Defensa, duelos, presión y recuperación de pelota',
  pages: '60 páginas',
  sections: [{
    title: '4.1 El 5 como especialista en cortar ataques',
    items: ['Cómo anticipar jugadas', 'Cómo cortar líneas de pase', 'Cómo interceptar sin barrerse', 'Cómo frenar ataques sin hacer falta', 'Cómo temporizar contragolpes', 'Cómo elegir entre presionar o retroceder']
  }, {
    title: '4.2 Marcaje del mediapunta rival',
    items: ['Cómo controlarlo sin perseguirlo', 'Cómo evitar que reciba entre líneas', 'Cómo usar referencias zonales', 'Cómo defender cuando se mueve a los costados', 'Cómo pasarlo a centrales o interiores', 'Cómo anticipar su giro']
  }, {
    title: '4.3 Cobertura a centrales',
    items: ['Cuándo cerrar la espalda del central', 'Cómo defender segundas jugadas', 'Cómo proteger la zona del 9', 'Cómo evitar que el rival ataque de frente', 'Cómo defender rechaces frontales', 'Cómo actuar cuando un central sale a banda']
  }, {
    title: '4.4 Cobertura a laterales',
    items: ['Cuándo caer a banda', 'Cómo defender al extremo rival', 'Cómo cerrar línea de pase interior', 'Cómo evitar quedar mano a mano innecesario', 'Cómo recuperar la posición central', 'Cómo coordinar con extremo o interior propio']
  }, {
    title: '4.5 Presión tras pérdida',
    items: ['Primeros 3 segundos después de perderla', 'Cómo activar la presión inmediata', 'Cómo cerrar pase hacia adelante', 'Cómo empujar al rival hacia banda', 'Cómo hacer falta táctica inteligente', 'Cuándo no presionar']
  }, {
    title: '4.6 Defensa de transiciones',
    items: ['Cómo frenar contragolpes', 'Cómo correr hacia atrás correctamente', 'Cómo orientar al atacante', 'Cómo ganar tiempo para que vuelva el equipo', 'Cómo defender inferioridad numérica', 'Cómo evitar quedar partido']
  }, {
    title: '4.7 Duelos individuales',
    items: ['Entrada frontal', 'Entrada lateral', 'Carga legal', 'Robo por anticipación', 'Robo por temporización', 'Defensa 1 vs 1 en zona central', 'Defensa 1 vs 1 cerca del área', 'Cuándo ir al suelo y cuándo no']
  }, {
    title: '4.8 Juego aéreo y segundas pelotas',
    items: ['Ubicación en saques largos', 'Cómo disputar de segunda línea', 'Cómo ganar rechaces', 'Cómo anticipar dónde caerá la pelota', 'Cómo proteger la frontal del área', 'Cómo transformar una segunda pelota en ataque']
  }, {
    title: '4.9 Faltas tácticas e inteligencia defensiva',
    items: ['Cuándo cortar una jugada', 'Dónde conviene hacer falta', 'Dónde no hacer falta nunca', 'Cómo evitar tarjetas innecesarias', 'Cómo usar el cuerpo sin excederse', 'Cómo cortar ritmo rival con inteligencia']
  }, {
    title: '4.10 Comunicación defensiva',
    items: ['Qué decirle a centrales', 'Qué decirle a laterales', 'Qué decirle a interiores', 'Cómo ordenar la presión', 'Cómo avisar marcas libres', 'Cómo corregir sin desconcentrarse']
  }, {
    title: '4.11 Ejercicios defensivos específicos',
    items: ['1 vs 1 frontal', '2 vs 2 con cobertura', '4 vs 3 en transición', 'Presión tras pérdida reducida', 'Interceptación de líneas de pase', 'Temporización de contraataques', 'Defensa del mediapunta', 'Coberturas laterales']
  }]
}, {
  title: 'Preparación física, entrenamiento invisible y plan de mejora del 5',
  pages: '60 páginas',
  sections: [{
    title: '5.1 Demandas físicas del mediocampista defensivo',
    items: ['Resistencia específica', 'Cambios de dirección', 'Aceleraciones cortas', 'Frenadas', 'Fuerza de piernas', 'Core y estabilidad', 'Potencia en duelos', 'Capacidad de repetir esfuerzos']
  }, {
    title: '5.2 Qué debe entrenar un 5',
    items: ['Resistencia aeróbica', 'Resistencia intermitente', 'Fuerza máxima', 'Fuerza funcional', 'Potencia de tren inferior', 'Velocidad corta', 'Agilidad defensiva', 'Movilidad de cadera y tobillo', 'Prevención de lesiones']
  }, {
    title: '5.3 Entrenamiento de fuerza para el 5',
    items: ['Sentadilla', 'Peso muerto', 'Zancadas', 'Hip thrust', 'Saltos', 'Core antirotacional', 'Trabajo unilateral', 'Fuerza excéntrica', 'Fuerza para contactos y duelos']
  }, {
    title: '5.4 Entrenamiento de velocidad y agilidad',
    items: ['Primeros 5 metros', 'Aceleración corta', 'Frenadas controladas', 'Cambios de dirección', 'Reacción a estímulos', 'Desplazamientos laterales', 'Giros defensivos', 'Carreras de recuperación']
  }, {
    title: '5.5 Resistencia específica de partido',
    items: ['Cómo correr como mediocampista, no como fondista', 'Series intermitentes', 'Repetición de esfuerzos intensos', 'Trabajo con pelota', 'Juegos reducidos', 'Bloques de alta intensidad', 'Cómo mejorar sin perder frescura']
  }, {
    title: '5.6 Prevención de lesiones para mediocampistas',
    items: ['Rodilla', 'Isquiotibiales', 'Aductores', 'Tobillo', 'Pubalgia', 'Sobrecarga lumbar', 'Fatiga acumulada', 'Entrada en calor específica']
  }, {
    title: '5.7 Preparación mental del 5',
    items: ['Concentración durante 90 minutos', 'Cómo no desconectarse cuando el equipo ataca', 'Manejo del error', 'Lectura emocional del partido', 'Liderazgo silencioso', 'Tomar decisiones bajo presión', 'Jugar con amarilla']
  }, {
    title: '5.8 Análisis de video para mejorar como 5',
    items: ['Qué mirar de tu propio partido', 'Cómo evaluar tu posicionamiento', 'Cómo detectar pérdidas peligrosas', 'Cómo medir tus coberturas', 'Cómo analizar tus decisiones', 'Cómo estudiar a mediocampistas profesionales']
  }, {
    title: '5.9 Plan de entrenamiento semanal',
    items: ['Semana tipo para jugador amateur', 'Semana tipo para jugador competitivo', 'Día de fuerza', 'Día de velocidad', 'Día de resistencia', 'Día técnico-táctico', 'Día de recuperación', 'Día de partido']
  }, {
    title: '5.10 Plan de mejora de 8 semanas',
    items: ['Semana 1: diagnóstico técnico y físico', 'Semana 2: perfil corporal y escaneo', 'Semana 3: posicionamiento defensivo', 'Semana 4: coberturas y transiciones', 'Semana 5: fuerza y duelos', 'Semana 6: salida limpia y pase vertical', 'Semana 7: presión tras pérdida', 'Semana 8: integración total en partido']
  }, {
    title: '5.11 Herramientas prácticas finales',
    items: ['Checklist prepartido del 5', 'Checklist postpartido', 'Tabla de autoevaluación', 'Mapa de zonas de influencia', 'Planilla de análisis de video', 'Planilla de carga semanal', 'Rutina de activación prepartido', 'Rutina de recuperación postpartido']
  }]
}];
export function Chapters() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const toggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };
  return <section className="py-12 px-4 bg-white" data-id="element-31">
      <div className="max-w-4xl mx-auto" data-id="element-32">
        <div className="text-center mb-8" data-id="element-33">
          <h2 className="font-display text-4xl md:text-6xl tracking-wide text-brand-text" data-id="element-34">
            5 CAPÍTULOS QUE{' '}
            <span className="text-brand-green" data-id="element-35">TRANSFORMAN</span> TU JUEGO
          </h2>
          <p className="text-brand-muted mt-3 text-sm md:text-base" data-id="element-36">
            Tocá cada capítulo para ver el contenido completo
          </p>
        </div>
        <div className="space-y-4" data-id="element-37">
          {chapters.map((chapter, index) => {
          const isOpen = openIndex === index;
          return <motion.div key={index} initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: index * 0.05
          }} className={`bg-white border rounded-xl overflow-hidden transition-colors shadow-sm ${isOpen ? 'border-brand-green shadow-md' : 'border-brand-border hover:border-brand-green/50'}`} data-id="element-38">
                <button onClick={() => toggle(index)} className="w-full p-6 flex items-center gap-6 text-left group" aria-expanded={isOpen} data-id="element-39">
                  <div className={`flex-shrink-0 w-14 h-14 md:w-16 md:h-16 rounded-full flex items-center justify-center border-2 transition-colors ${isOpen ? 'bg-brand-green text-white border-brand-green' : 'bg-white border-brand-green text-brand-green group-hover:bg-brand-greenSoft'}`} data-id="element-40">
                    <span className="font-display text-2xl md:text-3xl mt-1" data-id="element-41">
                      {index + 1}
                    </span>
                  </div>
                  <div className="flex-1" data-id="element-42">
                    <h3 className="text-base md:text-xl font-semibold text-brand-text leading-snug" data-id="element-43">
                      <span className="text-brand-green font-bold mr-2" data-id="element-44">
                        CAPÍTULO {index + 1} —
                      </span>
                      {chapter.title}
                    </h3>
                    <p className="text-xs md:text-sm text-brand-muted mt-1" data-id="element-45">
                      {chapter.pages}
                    </p>
                  </div>
                  <motion.div animate={{
                rotate: isOpen ? 180 : 0
              }} transition={{
                duration: 0.3
              }} className="flex-shrink-0 text-brand-green" data-id="element-46">
                    <ChevronDown className="w-6 h-6" data-id="element-47" />
                  </motion.div>
                </button>
                <AnimatePresence initial={false} data-id="element-48">
                  {isOpen && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3,
                ease: 'easeInOut'
              }} className="overflow-hidden" data-id="element-49">
                      <div className="px-6 pb-6 pt-2 border-t border-brand-border space-y-5 bg-brand-surface" data-id="element-50">
                        {chapter.sections.map((section, sIdx) => <div key={sIdx} data-id="element-51">
                            <h4 className="font-bold text-brand-green text-sm md:text-base mb-2 mt-4" data-id="element-52">
                              {section.title}
                            </h4>
                            <ul className="space-y-1.5 pl-4" data-id="element-53">
                              {section.items.map((item, iIdx) => <li key={iIdx} className="text-brand-text text-sm md:text-base relative before:content-[''] before:absolute before:left-[-14px] before:top-[10px] before:w-1.5 before:h-1.5 before:bg-brand-green before:rounded-full" data-id="element-54">
                                  {item}
                                </li>)}
                            </ul>
                          </div>)}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </motion.div>;
        })}
        </div>
      </div>
    </section>;
}